import {
  ArrowLeftOutlined, BarChartOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { AutomationStatus } from '@repo/utils';
import {
  Button, Divider, Flex, Skeleton, Tag, Typography,
} from 'antd';
import { AUTOMATION_EXECUTION_STATUS_CONFIG, AUTOMATION_STATUS_CONFIG } from 'automations/data/automationData';
import { AUTOMATION_CANVAS_PATH, AUTOMATION_REPORT_PATH, MANAGE_AUTOMATIONS_PATH } from 'automations/data/paths';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useAutomationId from 'automations/hooks/useAutomationId';
import {
  automationModeSelector,
  automationReportDataSelector,
  isReportPanelOpenSelector,
} from 'automations/selectors/automationConfig';
import { useGetAutomationDetailQuery } from 'automations/services/automationService';
import { setAutomationData, setReportPanelOpen } from 'automations/store/automationConfigSlice';
import { AutomationMode } from 'automations/types/automation';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import RenameAutomation from '../RenameAutomation';
import styles from './AutomationHeader.module.scss';
import LiveEdit from './LiveEdit';
import UpdateStatus from './UpdateStatus';

export default function AutomationHeader() {
  const id = useAutomationId();
  const navigate = useNavigate();
  const dispatch = useAutomationDispatch();
  const mode = useSelector(automationModeSelector);
  const reportPanelOpen = useSelector(isReportPanelOpenSelector);
  const reportData = useSelector(automationReportDataSelector);

  const [editMode, setEditMode] = useState<boolean>(false);

  const {
    currentData: data,
    isLoading,
  } = useGetAutomationDetailQuery(id, {
    skip: !id,
  });

  const handleGoBack = useCallback(() => {
    navigate(MANAGE_AUTOMATIONS_PATH);
  }, [navigate]);

  const {
    name,
    status,
    description = '',
  } = data?.automationList?.automations[0] ?? {};

  const statusConfig = useMemo(() => {
    if (mode === AutomationMode.liveEdit) {
      return {
        text: 'Live Edit',
        color: 'warning',
      };
    }
    return {
      text: status,
      color: status ? AUTOMATION_STATUS_CONFIG[status].color : undefined,
    };
  }, [mode, status]);

  useEffect(() => {
    if (data?.automationList?.automations[0]) {
      dispatch(setAutomationData(data.automationList.automations[0]));
    }
  }, [data, dispatch]);

  const handleViewReport = useCallback(() => {
    navigate(AUTOMATION_REPORT_PATH(id));
  }, [navigate, id]);

  const handleViewAutomation = useCallback(() => {
    navigate(AUTOMATION_CANVAS_PATH(id));
  }, [navigate, id]);

  const handleViewDetails = useCallback(() => {
    dispatch(setReportPanelOpen(true));
  }, [dispatch]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <Flex className={styles.leftContainer}>
          <Skeleton.Input active />
        </Flex>
      </div>
    );
  } else {
    return (
      <div className={styles.container}>
        <RenameAutomation
          isOpen={editMode}
          handleClose={() => setEditMode(false)}
          automationId={id}
          initialData={name ? {
            name,
            description,
          } : undefined}
        />

        <Flex className={styles.leftContainer}>
          <Button
            type="default"
            size="small"
            onClick={handleGoBack}
          >
            <ArrowLeftOutlined />
          </Button>

          <Typography.Title level={3} className={styles.title} onClick={() => setEditMode(true)}>
            {name}
          </Typography.Title>
          {statusConfig?.text && (
            <Tag color={statusConfig.color}>
              {statusConfig.text}
            </Tag>
          )}
          {(mode === AutomationMode.individualReport && reportData?.status) && (
            <Tag color={AUTOMATION_EXECUTION_STATUS_CONFIG[reportData.status]?.color}>
              {reportData.status}
            </Tag>
          )}
        </Flex>
        <Flex className={styles.rightContainer} gap={12}>
          {(mode === AutomationMode.default && status !== AutomationStatus.draft) && (
            <>
              <Button
                icon={<BarChartOutlined />}
                size="large"
                type="text"
                onClick={handleViewReport}
              >
                View Reports
              </Button>
              <Divider type="vertical" className={styles.divider} />
            </>
          )}
          <LiveEdit />
          {mode === AutomationMode.default && <UpdateStatus />}
          {([AutomationMode.commonReport, AutomationMode.individualReport].includes(mode!)) && (
            <>
              {!reportPanelOpen && (
                <Button
                  size="large"
                  type="text"
                  onClick={handleViewDetails}
                >
                  View Details
                </Button>
              )}
              <Button
                icon={<CloseCircleOutlined />}
                size="large"
                type="primary"
                onClick={handleViewAutomation}
              >
                Close Report
              </Button>
            </>
          )}
        </Flex>
      </div>
    );
  }
}
